import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { DIRECTUS_INSTANCE } from '../../../api';
import { fetchWidgetStatistics } from '../../../features/widgets/services/widgetApi';
import {
  getResolutionStatus,
  getSessionEffort,
  getSessionIntent,
  countSentimentDistribution,
  buildResolutionStats,
  buildEffortStats,
  buildPriorityStats,
  buildTrajectoryStats,
  buildRiskStats,
  buildLoopStats,
  filterJudgeScoredSessions,
  isJudgeConfidence,
  filterPipelineSessions
} from '../../../features/sentiment';
import { CURRENT_PIPELINE_VERSION, isCurrentPipelineSession } from '@prosecaptain/sentiment-rules';

/**
 * Hook to manage widget statistics (MVP v2)
 * 
 * Fetches and aggregates session analytics including:
 * - Basic metrics (sessions, messages, leads)
 * - Sentiment analysis (5-level with 3-level backward compat)
 * - Resolution / intent fulfillment stats
 * - Effort distribution (proxy CES)
 * - Priority distribution (P0-P3)
 * - Trajectory analysis (valence delta)
 * - Risk indicators (exit intent, closing missing)
 * - Qualitative data (topics, intents/categories)
 */
export const useWidgetStatistics = (widgetConfigId, dateRange, activeTab, workspaceId = null) => {
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsData, setStatsData] = useState({
    totalSessions: 0,
    totalMessages: 0,
    totalLeads: 0,
    sessionsByDay: [],
    messagesByDay: [],
    lastUpdated: null,

    // Sentiment (backward compat 3-level + new 5-level breakdown)
    sentimentStats: {
      positive: 0,
      negative: 0,
      neutral: 0,
      // 5-level breakdown
      very_positive: 0,
      very_negative: 0
    },

    // Resolution / Intent fulfillment
    resolutionStats: {
      resolved: 0,
      unresolved: 0,
      unknown: 0,
      total: 0,
      resolutionPercentage: 0,
      sessionsWithStatus: 0
    },

    // Effort distribution (proxy CES)
    effortStats: {
      distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
      average: 0,
      total: 0,
      highEffortCount: 0,      // effort >= 4
      highEffortPercentage: 0
    },

    // Priority distribution (P0-P3)
    priorityStats: {
      distribution: { P0: 0, P1: 0, P2: 0, P3: 0 },
      total: 0,
      actionRequired: 0,       // P0 + P1
      actionRequiredPercentage: 0
    },

    // Trajectory (valence change from opening to closing)
    trajectoryStats: {
      improving: 0,            // delta > 0
      declining: 0,            // delta < 0
      stable: 0,               // delta === 0
      noData: 0,               // delta === null
      averageDelta: 0,
      total: 0
    },

    // Risk indicators
    riskStats: {
      exitIntentCount: 0,
      exitIntentPercentage: 0,
      closingMissingCount: 0,
      closingMissingPercentage: 0,
      total: 0
    },

    // Qualitative (topics, intents, emotions - backward compat)
    qualitativeStats: {
      topTopics: [],
      topEmotions: [],
      topCategories: [],
      topIntents: [],
      totalSessionsWithTopics: 0,
      totalSessionsWithEmotions: 0,
      totalSessionsWithCategories: 0,
      totalSessionsWithIntents: 0
    },

    // Sentiment over time (for trend chart)
    sentimentByDay: [],

    // Loop detection stats
    loopStats: {
      loopCount: 0,
      loopPercentage: 0,
      total: 0
    },

    // Intent x Resolve cross-tab
    intentResolveStats: [],

    // AI confidence stats
    confidenceStats: {
      average: 0,
      total: 0,
      lowCount: 0,         // confidence < 0.6
      lowPercentage: 0
    }
  });

  const fetchStatistics = useCallback(async () => {
    if (!widgetConfigId) return;
    
    setStatsLoading(true);
    try {
      const token = localStorage.getItem('directus_token');
      if (!token) {
        setStatsLoading(false);
        return;
      }
      
      // Generate date range for filtering
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - dateRange + 1);
      
      // Fetch chart stats (workspaceId avoids redundant widget_config fetch)
      const stats = await fetchWidgetStatistics(widgetConfigId, token, dateRange, workspaceId);
      const resolvedWorkspaceId = workspaceId || stats.workspaceId;

      if (!resolvedWorkspaceId) {
        setStatsData(prev => ({ ...prev, ...stats }));
        return;
      }

      // Fetch sessions, sentiments, and widget sessions in parallel
      const [widgetSessionsResponse, sentimentsResponse] = await Promise.all([
        // Get widget sessions for leads
        axios.get(`${DIRECTUS_INSTANCE}/items/chat_sessions`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            filter: JSON.stringify({
              _and: [
                { linked_workspace: { _nnull: true } },
                { linked_workspace: { _eq: resolvedWorkspaceId } }
              ]
            }),
            fields: ['id'],
            limit: 1000
          }
        }),
        // Get analyzed sessions with all MVP fields.
        // Greeting-only / too-short sessions are dropped later by filterJudgeScoredSessions.
        axios.get(`${DIRECTUS_INSTANCE}/items/chat_sessions`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            filter: JSON.stringify({
              _and: [
                { linked_workspace: { _nnull: true } },
                { linked_workspace: { _eq: resolvedWorkspaceId } },
                { date_created: { _gte: startDate.toISOString() } },
                { date_created: { _lte: endDate.toISOString() } },
                { overall_sentiment: { _nnull: true } }
              ]
            }),
            fields: [
              'id',
              'date_created',
              'overall_sentiment',
              'resolution_status',
              'intent_fulfilled',
              'overall_sentiment_labels',
              'overall_sentiment_topics',
              'overall_sentiment_rationale',
              // MVP v2 fields
              'intensity_score',
              'effort_score',
              'business_risk',
              'priority_band',
              'primary_intent',
              'opening_valence',
              'closing_valence',
              'delta_valence',
              'delta_open_to_close',
              'closing_missing',
              'exit_intent',
              'confidence',
              'messages_count',
              'tags',
              'pipeline_version',
              'prompt_version',
              'title',
              'loop_detected',
              'no_progress_loop',
              'loop_count'
            ],
            limit: 1000
          }
        })
      ]);

      const widgetSessionIds = widgetSessionsResponse.data.data?.map(s => s.id) || [];
      
      // Fetch leads if we have sessions
      let totalLeads = 0;
      if (widgetSessionIds.length > 0) {
        try {
          const leadsResponse = await axios.get(`${DIRECTUS_INSTANCE}/items/crm`, {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              filter: JSON.stringify({
                linked_session: { _in: widgetSessionIds }
              }),
              meta: 'filter_count'
            }
          });
          totalLeads = leadsResponse.data.meta?.filter_count || 0;
        } catch (error) {
          // Silent error handling for leads
        }
      }

      // =====================================================================
      // AGGREGATE ALL STATS
      // =====================================================================
      const sessions = sentimentsResponse.data.data || [];
      const judged = filterJudgeScoredSessions(sessions);
      const hasCurrentPipeline = judged.some(isCurrentPipelineSession);
      const v2Sessions = hasCurrentPipeline
        ? filterPipelineSessions(judged, CURRENT_PIPELINE_VERSION)
        : judged;

      const { stats: sentimentStats } = countSentimentDistribution(v2Sessions);
      const resolutionStats = buildResolutionStats(v2Sessions);
      const effortStats = buildEffortStats(v2Sessions);
      const priorityStats = buildPriorityStats(v2Sessions);
      const trajectoryStats = buildTrajectoryStats(v2Sessions);
      const riskStats = buildRiskStats(v2Sessions);
      const loopStats = buildLoopStats(v2Sessions);

      // -----------------------------------------------------------------
      // 8. CONFIDENCE STATS (AI analysis quality)
      // -----------------------------------------------------------------
      let confidenceSum = 0;
      let confidenceCount = 0;
      let lowConfidenceCount = 0;

      v2Sessions.forEach(session => {
        if (!isJudgeConfidence(session)) return;
        const confNum = parseFloat(session.confidence);
        confidenceSum += confNum;
        confidenceCount++;
        if (confNum < 0.6) lowConfidenceCount++;
      });

      const confidenceStats = {
        average: confidenceCount > 0 ? (confidenceSum / confidenceCount).toFixed(2) : 0,
        total: confidenceCount,
        lowCount: lowConfidenceCount,
        lowPercentage: confidenceCount > 0
          ? ((lowConfidenceCount / confidenceCount) * 100).toFixed(1)
          : 0
      };

      // -----------------------------------------------------------------
      // 9. INTENT x RESOLVE CROSS-TAB
      // -----------------------------------------------------------------
      const intentResolveMap = {};
      v2Sessions.forEach(session => {
        const intent = getSessionIntent(session);
        if (!intent) return;

        if (!intentResolveMap[intent]) {
          intentResolveMap[intent] = {
            intent,
            total: 0,
            resolved: 0,
            partial: 0,
            unresolved: 0,
            effortSum: 0,
            effortCount: 0
          };
        }

        const entry = intentResolveMap[intent];
        entry.total++;

        // resolution_status maps: resolved=true, partially_resolved=partial, unresolved=false
        const res = getResolutionStatus(session);
        if (res === 'resolved') entry.resolved++;
        else if (res === 'partially_resolved') entry.partial++;
        else if (res === 'unresolved') entry.unresolved++;

        // effort
        const effort = getSessionEffort(session);
        if (effort && effort >= 1 && effort <= 5) {
          entry.effortSum += effort;
          entry.effortCount++;
        }
      });

      const intentResolveStats = Object.values(intentResolveMap)
        .map(entry => ({
          ...entry,
          resolveRate: entry.total > 0
            ? ((entry.resolved / entry.total) * 100).toFixed(1)
            : 0,
          avgEffort: entry.effortCount > 0
            ? (entry.effortSum / entry.effortCount).toFixed(1)
            : '-'
        }))
        .sort((a, b) => b.total - a.total);

      // -----------------------------------------------------------------
      // 9. QUALITATIVE STATS (topics, intents/categories, emotions)
      // -----------------------------------------------------------------
      const topicsMap = {};
      const emotionsMap = {};
      const categoriesMap = {};
      const intentsMap = {};
      
      v2Sessions.forEach(session => {
        // Extract topics from overall_sentiment_topics.topics
        if (session.overall_sentiment_topics?.topics) {
          const topics = session.overall_sentiment_topics.topics;
          (Array.isArray(topics) ? topics : []).forEach(topic => {
            if (topic && typeof topic === 'string') {
              topicsMap[topic] = (topicsMap[topic] || 0) + 1;
            }
          });
        }
        
        // Extract primary_intent (new MVP v2 field)
        const primaryIntent = getSessionIntent(session);
        if (primaryIntent && typeof primaryIntent === 'string') {
          intentsMap[primaryIntent] = (intentsMap[primaryIntent] || 0) + 1;
        }
        
        // Extract primary_category (backward compat - old data + new data via compat key)
        const primaryCategory = session.overall_sentiment_topics?.primary_category;
        if (primaryCategory && typeof primaryCategory === 'string') {
          categoriesMap[primaryCategory] = (categoriesMap[primaryCategory] || 0) + 1;
        }
        
        // Extract emotions from old-format data (key_emotions in rationale)
        // New MVP v2 data doesn't have key_emotions - this handles legacy data
        let rationale = session.overall_sentiment_rationale;
        if (typeof rationale === 'string') {
          try { rationale = JSON.parse(rationale); } catch (e) { rationale = null; }
        }
        if (rationale?.key_emotions && Array.isArray(rationale.key_emotions)) {
          rationale.key_emotions.forEach(emotion => {
            if (emotion && typeof emotion === 'string') {
              emotionsMap[emotion] = (emotionsMap[emotion] || 0) + 1;
            }
          });
        }
        
        // Extract from overall_sentiment_labels (old: emotions, new: topics)
        if (session.overall_sentiment_labels && Array.isArray(session.overall_sentiment_labels)) {
          session.overall_sentiment_labels.forEach(label => {
            if (label && typeof label === 'string') {
              // For backward compat, still count these in emotions
              emotionsMap[label] = (emotionsMap[label] || 0) + 1;
            }
          });
        }
      });
      
      // Sort and get top entries
      const sortAndSlice = (map, limit = 10) => 
        Object.entries(map)
          .sort((a, b) => b[1] - a[1])
          .slice(0, limit)
          .map(([name, count]) => ({ name, count }));
      
      const topTopics = sortAndSlice(topicsMap, 10);
      const topEmotions = sortAndSlice(emotionsMap, 10);
      const topCategories = sortAndSlice(categoriesMap);
      const topIntents = sortAndSlice(intentsMap);
      
      const qualitativeStats = {
        topTopics,
        topEmotions,
        topCategories,
        topIntents,
        totalSessionsWithTopics: Object.keys(topicsMap).length > 0
          ? v2Sessions.filter(s => s.overall_sentiment_topics?.topics?.length > 0).length
          : 0,
        totalSessionsWithEmotions: Object.keys(emotionsMap).length > 0
          ? v2Sessions.filter(s => {
              let rat = s.overall_sentiment_rationale;
              if (typeof rat === 'string') { try { rat = JSON.parse(rat); } catch (e) { rat = null; } }
              return rat?.key_emotions?.length > 0 || s.overall_sentiment_labels?.length > 0;
            }).length
          : 0,
        totalSessionsWithCategories: Object.keys(categoriesMap).length > 0
          ? v2Sessions.filter(s => s.overall_sentiment_topics?.primary_category).length
          : 0,
        totalSessionsWithIntents: Object.keys(intentsMap).length > 0
          ? v2Sessions.filter(s => s.overall_sentiment_topics?.primary_intent).length
          : 0
      };

      // -----------------------------------------------------------------
      // 8. SENTIMENT OVER TIME (for trend chart)
      // -----------------------------------------------------------------
      const SENTIMENT_SCORE = {
        'very_positive': 1.0,
        'positive': 0.5,
        'neutral': 0.0,
        'negative': -0.5,
        'very_negative': -1.0
      };

      const dayMap = {};
      sessions.forEach(s => {
        const day = s.date_created?.split('T')[0];
        if (!day) return;
        if (!dayMap[day]) dayMap[day] = { scores: [], positive: 0, neutral: 0, negative: 0, total: 0 };

        // Use closing_valence (continuous) if available, else map sentiment to score
        const score = (s.closing_valence !== null && s.closing_valence !== undefined)
          ? parseFloat(s.closing_valence)
          : (SENTIMENT_SCORE[s.overall_sentiment] ?? null);

        if (score !== null && !isNaN(score)) dayMap[day].scores.push(score);

        const sent = s.overall_sentiment;
        if (sent === 'very_positive' || sent === 'positive') dayMap[day].positive++;
        else if (sent === 'very_negative' || sent === 'negative') dayMap[day].negative++;
        else dayMap[day].neutral++;
        dayMap[day].total++;
      });

      // Generate all dates in range so chart has no gaps
      const sentimentByDay = [];
      const dayCursor = new Date(startDate);
      while (dayCursor <= endDate) {
        const key = dayCursor.toISOString().split('T')[0];
        const data = dayMap[key];
        const month = dayCursor.toLocaleDateString('en-US', { month: 'short' });
        const dayNum = dayCursor.getDate();

        sentimentByDay.push({
          date: key,
          displayDate: `${month} ${dayNum}`,
          avgSentiment: data && data.scores.length > 0
            ? parseFloat((data.scores.reduce((a, b) => a + b, 0) / data.scores.length).toFixed(2))
            : null,
          positive: data?.positive || 0,
          neutral: data?.neutral || 0,
          negative: data?.negative || 0,
          total: data?.total || 0
        });

        dayCursor.setDate(dayCursor.getDate() + 1);
      }

      // =====================================================================
      // SET ALL STATS
      // =====================================================================
      // Override totalSessions with the sentiment query count (authoritative).
      // fetchWidgetStatistics creates its own Date objects, which can cause
      // a 1-session boundary discrepancy vs. the sentiment query above.
      // All detailed stats (sentiment, resolution, effort, etc.) are computed
      // from `sessions`, so totalSessions must match sessions.length.
      const totalAnalyzedSessions = sessions.length;

      // Recompute totalMessages from sessions that have messages_count (MVP v2).
      // Fall back to stats.totalMessages for sessions missing this field.
      const v2MessageSum = sessions.reduce((acc, s) => acc + (s.messages_count || 0), 0);
      const consistentTotalMessages = v2MessageSum > 0 ? v2MessageSum : stats.totalMessages;

      setStatsData({
        ...stats,
        totalSessions: totalAnalyzedSessions,
        totalMessages: consistentTotalMessages,
        totalAnalyzedSessions,
        totalLeads,
        sentimentStats,
        resolutionStats,
        effortStats,
        priorityStats,
        trajectoryStats,
        riskStats,
        loopStats,
        confidenceStats,
        intentResolveStats,
        qualitativeStats,
        sentimentByDay
      });
      
    } catch (error) {
      // Silent error handling
      console.error('[useWidgetStatistics] Error fetching statistics:', error);
    } finally {
      setStatsLoading(false);
    }
  }, [widgetConfigId, dateRange, workspaceId]);

  useEffect(() => {
    if (activeTab === 'statistics' && widgetConfigId) {
      fetchStatistics();
    }
  }, [activeTab, widgetConfigId, dateRange, fetchStatistics]);

  return {
    statsLoading,
    statsData,
    fetchStatistics
  };
};
